import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { forwardRef, useCallback, useRef } from 'react';
import { Calendar as CalendarIcon, List as ListIcon } from 'lucide-react';
import { useAnimationFrameCallback } from '../../../hooks/useAnimationFrameCallback';
const DEFAULT_ARIA_LABEL = 'View mode';
const CONTAINER_CLASSES = [
    'inline-flex items-center gap-1',
    'rounded-full border border-white/[0.14]',
    'bg-white/[0.08] backdrop-blur-[14px]',
    'p-1',
].join(' ');
// Active styling flows through the design-token layer: the amber fill is read
// from `--loci-color-primary` with a cascade fallback to `--color-accent` and
// a hard-coded hex. Tailwind arbitrary values reference these CSS variables
// so theming and overrides stay in the token layer rather than inline styles.
const TAB_CLASSES = [
    'inline-flex items-center justify-center gap-2',
    'min-h-[44px] min-w-[44px] px-4 py-2',
    'rounded-full',
    'text-[13px] font-semibold',
    'transition-colors duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60 focus-visible:ring-offset-2 focus-visible:ring-offset-transparent',
    'disabled:cursor-not-allowed disabled:opacity-50',
    // Inactive state.
    'data-[active=false]:text-white data-[active=false]:hover:bg-white/[0.08]',
    // Active state — amber accent token cascade.
    'data-[active=true]:bg-[var(--loci-color-primary,var(--color-accent,#F4A12B))]',
    'data-[active=true]:text-[#111111]',
].join(' ');
const TABS = [
    { value: 'list', label: 'List', icon: _jsx(ListIcon, { size: 16, "aria-hidden": "true" }) },
    { value: 'calendar', label: 'Calendar', icon: _jsx(CalendarIcon, { size: 16, "aria-hidden": "true" }) },
];
/**
 * ViewToggle — pill segmented control with two options (List / Calendar).
 *
 * Controlled component: the parent owns `value` and updates it via `onChange`.
 * Implements the WAI-ARIA tablist pattern with roving `tabIndex`, arrow-key
 * navigation, and Enter/Space activation.
 *
 * Active state fills with the LOCI amber accent token; inactive state uses
 * glassmorphism inherited from the container.
 */
export const ViewToggle = forwardRef(({ value, onChange, disabled = false, ariaLabel = DEFAULT_ARIA_LABEL, className = '', ...rest }, ref) => {
    const buttonRefs = useRef([]);
    const scheduleFrame = useAnimationFrameCallback();
    const focusTabAt = useCallback((index) => {
        scheduleFrame(() => {
            buttonRefs.current[index]?.focus();
        });
    }, [scheduleFrame]);
    const handleKeyDown = useCallback((event) => {
        if (disabled)
            return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')
            return;
        event.preventDefault();
        const currentIndex = TABS.findIndex((tab) => tab.value === value);
        const delta = event.key === 'ArrowRight' ? 1 : -1;
        const nextIndex = (currentIndex + delta + TABS.length) % TABS.length;
        const next = TABS[nextIndex];
        onChange(next.value);
        focusTabAt(nextIndex);
    }, [disabled, focusTabAt, onChange, value]);
    const containerClasses = [CONTAINER_CLASSES, className].filter(Boolean).join(' ');
    return (_jsx("div", { ref: ref, role: "tablist", "aria-label": ariaLabel, "data-testid": "view-toggle", onKeyDown: handleKeyDown, className: containerClasses, ...rest, children: TABS.map((tab, index) => {
            const isActive = tab.value === value;
            return (_jsxs("button", { ref: (el) => {
                    buttonRefs.current[index] = el;
                }, type: "button", role: "tab", "aria-selected": isActive, "aria-label": tab.label, tabIndex: isActive ? 0 : -1, disabled: disabled, "data-testid": `view-toggle-${tab.value}`, "data-active": isActive ? 'true' : 'false', className: TAB_CLASSES, onClick: () => {
                    if (!isActive)
                        onChange(tab.value);
                }, children: [_jsx("span", { className: "inline-flex items-center", "aria-hidden": "true", children: tab.icon }), _jsx("span", { children: tab.label })] }, tab.value));
        }) }));
});
ViewToggle.displayName = 'ViewToggle';
